import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/react'
import {AnimatePresence, motion} from 'framer-motion'
import {forwardRef} from 'react'
import {useBoolean} from 'usehooks-ts'

import {twMerge} from '@sdox/utils'
import {IconMinus, IconPlus} from '@sdox/icons'

import {RichText} from '../RichText'
import styles from './accordion.module.scss'

import type {ComponentProps, ReactNode} from 'react'
import type {ClassNameValue} from 'tailwind-merge'

export interface AccordionProps {
  title: string
  open?: boolean
  children?: ReactNode
  btnProps?: Pick<ComponentProps<'button'>, 'className'>
  actions?: ReactNode
  panelClassName?: ClassNameValue
}

export const Accordion = forwardRef<HTMLButtonElement, AccordionProps>((props, ref) => {
  const {
    title,
    children,
    open = false,
    btnProps = {},
    panelClassName,
  } = props
  const {value: isAnimating, setTrue: onAnimationStart, setFalse: onAnimationComplete} = useBoolean()

  return (
    <Disclosure defaultOpen={open}>
      {({open}) => (
        <>
          <DisclosureButton
            ref={ref}
            tabIndex={0}
            {...btnProps}
            className={twMerge(
              'box-border flex w-full cursor-pointer select-none flex-row justify-between text-left',
              'border-t border-outline-secondary text-interactive-glyph-default-primary last:border-b',
              'py-spacing-20 @block-medium:py-spacing-24',
              // eslint-disable-next-line max-len
              // 'focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
              btnProps.className,
            )}
          >
            <RichText
              as={'h4'}
              className={'line-clamp-2 py-spacing-4 text-h4-xs font-medium @block-medium:py-spacing-2 @block-medium:text-h4'}
              content={title}
              allowedTags={['span']}
            />
            <div className={'ml-spacing-24 flex flex-row gap-spacing-8'}>
              {open ? <IconMinus className={'size-spacing-32'} /> : <IconPlus className={'size-spacing-32'} />}
            </div>
          </DisclosureButton>
          <AnimatePresence>
            {open && (
              <motion.div
                onAnimationStart={onAnimationStart}
                onAnimationComplete={onAnimationComplete}
                style={{overflowY: isAnimating ? 'hidden' : undefined}}
                initial={'hidden'}
                animate={'visible'}
                exit={'hidden'}
                variants={{
                  visible: {height: 'auto', opacity: 1},
                  hidden: {height: 0, opacity: 0},
                }}
              >
                <DisclosurePanel
                  className={twMerge(
                    'flex flex-col gap-spacing-32 border-t border-outline-secondary text-p1',
                    '@block-medium:px-spacing-20 @block-medium:py-spacing-32',
                    'py-spacing-24',
                    panelClassName,
                    styles.accordion,
                  )}
                  static
                >
                  {children}
                </DisclosurePanel>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </Disclosure>
  )
})
